/**
 * App Store Release — is this clinic's iOS app live, as far as its portal
 * knows? Read-only: the three env vars are edited in the Environment Variables
 * section, and like any env change they need a deploy to reach the portal.
 *
 * State comes from which of APPLE_TEAM_ID / IOS_BUNDLE_ID / IOS_APP_STORE_ID are
 * set; the store id is also checked against Apple's public listing
 * (`backend/src/services/expressPortals/appStoreRelease.js`). Most clinics have
 * no app, so "Not live" is neutral — only a broken setup warns.
 */

import { useCallback, useEffect, useState } from 'react'
import { RefreshCw, Loader2 } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Badge } from '../ui'
import { expressPortalsAPI } from '../../services/api'

function apiErrorMessage(err, fallback) {
  return err?.response?.data?.error?.message || fallback
}

const STATE_BADGE = {
  'not-live': { variant: 'secondary', label: 'Not live' },
  'in-development': { variant: 'info', label: 'In development' },
  live: { variant: 'success', label: 'Live' },
  misconfigured: { variant: 'warning', label: 'Misconfigured' },
}

const STATE_DETAIL = {
  'not-live': 'No iOS app is configured for this clinic.',
  'in-development': 'The app is set up but has no App Store id yet, so mobile Safari shows no banner.',
  live: 'Mobile Safari shows the "get the app" banner on the portal.',
  misconfigured: 'Some of the iOS variables are set and others are not. Set all three, or none.',
}

const VAR_LABELS = {
  APPLE_TEAM_ID: 'Apple team id',
  IOS_BUNDLE_ID: 'iOS bundle id',
  IOS_APP_STORE_ID: 'App Store id',
}

/** Listing check -> the warning shown under it. `pass` and `skipped` show none. */
function listingWarning(listing, vars) {
  switch (listing?.status) {
    case 'not-found':
      return 'Apple has no public listing for this App Store id. The banner will not show until the app is released.'
    case 'invalid-id':
      return 'IOS_APP_STORE_ID is not an App Store id (it should be the digits after "id" in the App Store URL). The portal emits no banner.'
    case 'bundle-mismatch':
      return `This App Store id belongs to ${listing.app?.bundleId || 'another app'}, not ${vars.IOS_BUNDLE_ID.value}. Check the id was not copied from another clinic.`
    case 'bundle-unknown':
      return 'Listed in the App Store, but IOS_BUNDLE_ID is not readable, so the listing could not be matched to this clinic.'
    case 'error':
      return 'Could not check the App Store listing. Refresh to try again.'
    default:
      return null
  }
}

export default function AppStoreReleaseSection({ clinicId, onEditEnvVars, onDeployNow }) {
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)

  const loadStatus = useCallback(
    async (bypassCache = false) => {
      setLoading(true)
      setLoadError(null)
      try {
        const res = await expressPortalsAPI.getAppStoreRelease(clinicId, bypassCache)
        setStatus(res.data.data)
      } catch (err) {
        setLoadError(apiErrorMessage(err, 'Unable to load App Store release status'))
      } finally {
        setLoading(false)
      }
    },
    [clinicId]
  )

  useEffect(() => {
    setStatus(null)
    loadStatus()
  }, [loadStatus])

  const badge = status && STATE_BADGE[status.state]
  const app = status?.listing?.app
  const warning = status && listingWarning(status.listing, status.vars)

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2">
              App Store Release
              {badge && <Badge variant={badge.variant}>{badge.label}</Badge>}
            </CardTitle>
            <CardDescription>
              Whether this clinic&apos;s iOS app is live, from its iOS env vars and its public App Store listing.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => loadStatus(true)} disabled={loading}>
            <RefreshCw className={`h-3 w-3 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {loading && !status && (
          <div className="flex items-center text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 mr-2 animate-spin" />
            Checking the App Store…
          </div>
        )}

        {loadError && <div className="text-xs text-destructive">{loadError}</div>}

        {status && (
          <>
            <div className={`text-xs ${status.state === 'misconfigured' ? 'text-orange-600' : 'text-muted-foreground'}`}>
              {STATE_DETAIL[status.state]}
            </div>

            <div className="space-y-1">
              {Object.entries(VAR_LABELS).map(([key, label]) => {
                const entry = status.vars?.[key]
                return (
                  <div key={key} className="grid grid-cols-[1.5rem_9rem_1fr] gap-2 text-xs items-baseline">
                    <span className={entry?.set ? 'text-green-600' : 'text-muted-foreground'}>{entry?.set ? '✓' : '–'}</span>
                    <span className="text-muted-foreground">{label}</span>
                    <span className="break-all font-mono">
                      {entry?.set ? entry.value || 'set (hidden)' : <span className="font-sans text-muted-foreground">not set</span>}
                    </span>
                  </div>
                )
              })}
            </div>

            {app && (
              <div className="flex items-center gap-3 border-t pt-3">
                {app.iconUrl && <img src={app.iconUrl} alt="" className="h-10 w-10 rounded-lg border" />}
                <div className="text-xs space-y-0.5">
                  <div className="font-medium">{app.name || 'Unnamed app'}</div>
                  <div className="text-muted-foreground font-mono">{app.bundleId}</div>
                  {app.storeUrl && (
                    <a href={app.storeUrl} target="_blank" rel="noreferrer" className="underline">
                      Open in App Store
                    </a>
                  )}
                </div>
              </div>
            )}

            {warning && <div className="text-xs text-orange-600">{warning}</div>}
          </>
        )}

        <div className="flex flex-wrap items-center gap-2 border-t pt-3">
          <Button variant="outline" size="sm" onClick={onEditEnvVars}>
            Edit env vars
          </Button>
          <Button variant="outline" size="sm" onClick={onDeployNow}>
            Redeploy
          </Button>
          <span className="text-xs text-muted-foreground">Env changes reach the portal only after a deploy.</span>
        </div>
      </CardContent>
    </Card>
  )
}
